import clsx from 'clsx';
import { useController } from 'react-hook-form';
import Button from 'components/form/Button';
import { ChangeEvent, InputHTMLAttributes, LegacyRef, ReactNode } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  color?: string;
  containerClass?: string;
  rounded?: string;
  control: any;
  name: string;
  handleChange?: (value: string, name: string) => void;
}
interface WithoutControlProps extends Omit<InputProps, 'control'> {
  error?: { message: string };
  ref?: LegacyRef<HTMLInputElement>;
  handleSearch?: () => void;
}

export const InputWithStyle = ({ ...props }) => {
  return (
    <InputField
      {...props}
      containerClass="brand-border-light brand-shadow bg-white"
      className="bg-white"
    />
  );
};

const InputField = ({
  startIcon,
  endIcon,
  className,
  containerClass,
  rounded = 'rounded-md',
  name,
  control,
  defaultValue,
  handleChange,
  disabled,
  ...props
}: any) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: control,
    defaultValue: defaultValue || '',
  });

  const onChange = (e: any, field: any) => {
    field?.onChange(e.target.value);
    handleChange?.(e.target.value, e.target.name);
  };

  return (
    <div className="max-h-[45px]">
      <div
        className={clsx(
          'flex items-center gap-x-3 border py-1 md:py-0',
          containerClass,
          rounded,
          startIcon ? 'px-4' : 'px-2',
          disabled && 'bg-zinc-50'
        )}
      >
        {startIcon ? startIcon : null}

        <input
          {...field}
          {...props}
          onChange={(e) => onChange(e, field)}
          name={name}
          className={clsx(
            'w-full bg-inherit px-1 py-1 outline-none md:py-2',
            className
          )}
        />

        {endIcon ? endIcon : null}
      </div>
      {error && (
        <small className="text-[10px] text-red-500">{error?.message}</small>
      )}
    </div>
  );
};

export const InputWithoutControl = ({
  startIcon,
  endIcon,
  className,
  containerClass,
  rounded = 'rounded-md',
  name,
  defaultValue,
  handleChange,
  ...props
}: any) => {
  const onChange = (e: any) => {
    handleChange?.(e.target.value, e.target.name);
  };

  return (
    <div className="max-h-[45px]">
      <div
        className={clsx(
          'flex items-center gap-x-3 border py-1 md:py-0',
          containerClass,
          rounded,
          startIcon ? 'px-4' : 'px-2'
        )}
      >
        {startIcon ? startIcon : null}

        <input
          onChange={(e) => onChange(e)}
          name={name}
          className={clsx('w-full px-1 py-1 outline-none md:py-2', className)}
          {...props}
        />

        {endIcon ? endIcon : null}
      </div>
      {/* {error && (
        <small className="text-[10px] text-red-500">{error?.message}</small>
      )} */}
    </div>
  );
};

export default InputField;

export const SearchInput = ({
  handleChange,
  containerClass,
  className,
  startIcon,
  rounded,
  handleSearch,
  placeholder = 'Search...',
  ...props
}: WithoutControlProps) => {
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleChange?.(e.target.value, e.target.name);
  };

  const onSearchClick = () => {
    handleSearch?.();
  };

  return (
    <div
      className={clsx(
        'brand-border-light focus-within:border-brand-blue dark:bg-brand-dark-200 relative flex w-full items-center gap-x-3 rounded-l-full rounded-r-full border bg-white py-2 placeholder-gray-400 md:w-72',
        containerClass,
        rounded,
        startIcon ? 'px-4' : 'px-2'
      )}
    >
      <span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="h-6 w-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
          />
        </svg>
      </span>
      <input
        {...props}
        placeholder={placeholder}
        onChange={onChange}
        className={clsx(
          'w-full bg-inherit  px-1 pr-16 outline-none md:text-[13px]',
          className
        )}
      />
      <Button
        className="absolute inset-y-0 right-0 rounded-r-full bg-blue-500 px-2 text-sm text-white"
        onClick={() => onSearchClick}
      >
        search
      </Button>
    </div>
  );
};
